// src/hooks/useFirestore.js
// Custom hooks để đọc/ghi Firestore

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";

// ── Workspaces ─────────────────────────────────────────────

export function useWorkspaces() {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "workspaces"),
      where("members", "array-contains", user.uid)
    );
    const unsub = onSnapshot(q, (snap) => {
      setWorkspaces(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user]);

  const createWorkspace = async (name, description = "") => {
    const ws = await addDoc(collection(db, "workspaces"), {
      name,
      description,
      ownerId: user.uid,
      members: [user.uid],
      createdAt: serverTimestamp(),
    });
    return ws.id;
  };

  // Xóa workspace + toàn bộ tasks bên trong (chỉ owner)
  const deleteWorkspace = async (workspaceId) => {
    const batch = writeBatch(db);
    // Xóa tất cả tasks thuộc workspace này
    const tasksSnap = await getDocs(
      query(collection(db, "tasks"), where("workspaceId", "==", workspaceId))
    );
    tasksSnap.forEach((d) => batch.delete(d.ref));
    // Xóa workspace
    batch.delete(doc(db, "workspaces", workspaceId));
    await batch.commit();
  };

  return { workspaces, loading, createWorkspace, deleteWorkspace };
}

// ── Tasks ──────────────────────────────────────────────────

export function useTasks(workspaceId) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    const q = query(
      collection(db, "tasks"),
      where("workspaceId", "==", workspaceId),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [workspaceId]);

  const createTask = async (data) => {
    await addDoc(collection(db, "tasks"), {
      ...data,
      workspaceId,
      createdBy: user.uid,
      status: "todo",
      createdAt: serverTimestamp(),
    });
  };

  const updateTask = async (taskId, data) => {
    await updateDoc(doc(db, "tasks", taskId), {
      ...data,
      updatedAt: serverTimestamp(),
    });
  };

  const deleteTask = async (taskId) => {
    await deleteDoc(doc(db, "tasks", taskId));
  };

  return { tasks, loading, createTask, updateTask, deleteTask };
}

// ── Workspace members ──────────────────────────────────────

export function useMembers(memberIds = []) {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!memberIds.length) return;
    const unsubs = memberIds.map((uid) =>
      onSnapshot(doc(db, "users", uid), (snap) => {
        if (snap.exists()) {
          setMembers((prev) => {
            const filtered = prev.filter((m) => m.uid !== uid);
            return [...filtered, { uid, ...snap.data() }];
          });
        }
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [memberIds.join(",")]);

  return members;
}
